/**
 * Thin client for the Crave API.
 *
 * Every call is relative to `/api`, which the Vite dev server proxies to the
 * Express workspace and which production serves from the same origin. No
 * component should ever need to know the backend's hostname.
 */

const BASE = '/api';

/** Error carrying the HTTP status so callers can distinguish 503 from 500. */
export class ApiError extends Error {
  /** @param {number} status @param {string} message */
  constructor(status, message) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * @param {string} path
 * @param {RequestInit} [init]
 * @returns {Promise<any>}
 */
async function request(path, init) {
  const response = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiError(response.status, payload.error || `Request failed (${response.status})`);
  }

  return payload;
}

/**
 * Fetches live Google Places enrichment for one restaurant.
 *
 * Returns `null` rather than throwing when the integration is switched off, so
 * cards can quietly fall back to bundled placeholder content.
 *
 * @param {string} name
 * @param {number|null} [restaurantId]  Lets the server skip the name search.
 * @returns {Promise<import('./types.js').PlaceDetails | null>}
 */
export async function fetchPlaceDetails(name, restaurantId = null, googlePlaceId = null) {
  try {
    if (googlePlaceId) {
      return await request(`/places/details?placeId=${encodeURIComponent(googlePlaceId)}`);
    }
    const id = restaurantId ? `&id=${encodeURIComponent(restaurantId)}` : '';
    return await request(`/places/details?name=${encodeURIComponent(name)}${id}`);
  } catch (error) {
    if (error instanceof ApiError) return null;
    throw error;
  }
}

/** Searches Google's live restaurant index for group ballot setup. */
export async function searchRestaurants(query, coords, signal) {
  const params = new URLSearchParams({ query });
  if (coords) {
    params.set('lat', String(coords[0]));
    params.set('lng', String(coords[1]));
  }
  const { restaurants } = await request(`/places/search?${params}`, { signal });
  return Array.isArray(restaurants) ? restaurants : [];
}

/** Resolves searched group venues from the Place IDs stored in the session. */
export async function fetchRestaurantsByPlaceIds(placeIds) {
  if (placeIds.length === 0) return [];
  try {
    const { restaurants } = await request('/places/group-deck', {
      method: 'POST',
      body: JSON.stringify({ placeIds }),
    });
    return Array.isArray(restaurants) ? restaurants : null;
  } catch {
    return null;
  }
}

/**
 * Fetches the server-side restaurant catalogue.
 *
 * Returns `null` when the catalogue isn't configured or the request fails, so
 * the caller can fall back to the bundled placeholder list rather than showing
 * an empty deck — same contract as `fetchPlaceDetails`.
 *
 * @param {object} [options]
 * @param {string} [options.city]
 * @returns {Promise<import('./types.js').Restaurant[] | null>}
 */
export async function fetchRestaurants({ city, deviceId, coords } = {}) {
  try {
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    // Sending a device id opts into personalised ordering; without it the
    // endpoint stays publicly cacheable and returns the same deck to everyone.
    if (deviceId) params.set('deviceId', deviceId);
    if (coords) {
      params.set('lat', String(coords[0]));
      params.set('lng', String(coords[1]));
    }
    // Local calendar day so the deck rotates at the person's midnight, not UTC.
    params.set('day', localCalendarDay());

    const query = params.toString();
    const { restaurants } = await request(`/restaurants${query ? `?${query}` : ''}`);
    return Array.isArray(restaurants) && restaurants.length > 0 ? restaurants : null;
  } catch {
    return null;
  }
}

/**
 * The exact restaurants a group host's deck holds, in order. Null on failure.
 *
 * @param {number[]} ids
 * @returns {Promise<import('./types.js').Restaurant[] | null>}
 */
export async function fetchRestaurantsByIds(ids) {
  if (ids.length === 0) return [];
  try {
    const { restaurants } = await request(`/restaurants/group-deck?ids=${ids.join(',')}`);
    return Array.isArray(restaurants) ? restaurants : null;
  } catch {
    return null;
  }
}

/**
 * Reports swipes and searches so the deck can learn.
 *
 * Fire-and-forget: recommendation quality is not worth interrupting anyone's
 * session over, and a dropped batch costs a little personalisation rather than
 * anything the person notices.
 *
 * @param {{deviceId: string, swipes?: object[], searches?: string[]}} batch
 */
export function sendEvents(batch) {
  request('/events', { method: 'POST', body: JSON.stringify(batch) }).catch(() => {});
}

/**
 * Promoted cards currently scheduled to appear in the deck. Returns `null` on
 * any failure, which the caller treats as "no ads" rather than an error.
 *
 * @returns {Promise<object[] | null>}
 */
export async function fetchPromoted(coords) {
  if (!coords) return [];
  try {
    // The server only serves cards for the city these coordinates are in.
    const { promoted } = await request(`/promoted?lat=${coords[0]}&lng=${coords[1]}`);
    return Array.isArray(promoted) ? promoted : null;
  } catch {
    return null;
  }
}

/**
 * Real walk/drive minutes between two points, via the server's Routes API
 * proxy. Returns `null` on any failure — the caller falls back to a local
 * estimate, so this never needs to distinguish "not configured" from "the
 * request failed."
 *
 * @param {[number, number]} origin
 * @param {[number, number]} destination
 * @returns {Promise<{walkMinutes: number|null, driveMinutes: number|null} | null>}
 */
export async function getTravelTime(origin, destination) {
  try {
    return await request(
      `/travel-time?originLat=${origin[0]}&originLng=${origin[1]}` +
        `&destLat=${destination[0]}&destLng=${destination[1]}`
    );
  } catch {
    return null;
  }
}

/**
 * Builds the proxied URL for a Places photo. The bytes stream through our
 * server so the Google key is never in the browser.
 *
 * @param {string} ref
 * @returns {string}
 */
export function placePhotoUrl(ref) {
  return `${BASE}/places/photo?ref=${encodeURIComponent(ref)}`;
}

/**
 * Exchanges a Google ID token for a session — the server sets the session
 * cookie on success.
 *
 * @param {string} idToken
 * @returns {Promise<{user: import('./types.js').CurrentUser}>}
 */
export function signInWithGoogle(idToken) {
  return request('/auth/google', { method: 'POST', body: JSON.stringify({ idToken }) });
}

/**
 * Resolves the currently signed-in user. Returns `null` for any failure —
 * signed out, session expired, accounts not configured, or the network
 * request itself failing (offline, unreachable API) — since none of those
 * cases need different UI treatment, and the sole caller has no fallback
 * path if this rejects instead.
 *
 * @returns {Promise<import('./types.js').CurrentUser | null>}
 */
export async function getCurrentUser() {
  try {
    const { user } = await request('/auth/me');
    return user;
  } catch {
    return null;
  }
}

/**
 * @returns {Promise<{ok: boolean, integrations: Record<string, boolean>} | null>}
 */
export async function getHealth() {
  try {
    return await request('/health');
  } catch {
    return null;
  }
}

/** Ends the session server-side and clears the cookie. */
export function logout() {
  return request('/auth/logout', { method: 'POST' });
}

/**
 * Permanently deletes the signed-in account, its saved plans, and every
 * session it holds. Unlike most calls here this one propagates its error —
 * silently failing to delete an account would be the worst possible outcome,
 * since the person would believe their data was gone when it wasn't.
 */
export function deleteAccount() {
  return request('/auth/me', { method: 'DELETE' });
}

/**
 * Adds a phone number to the launch waitlist.
 *
 * Unlike most calls here this one lets its error through. Every other fetcher
 * swallows failures because it has a fallback — placeholder cards, a local
 * estimate, an empty list. This one has none: the person typed something and
 * is waiting to be told whether it worked, and quietly resolving would show
 * them a success state for a number that was never stored.
 *
 * @param {string} phone     As typed; the server normalises it to E.164.
 * @param {string} [source]  Which surface the signup came from.
 * @returns {Promise<{position: number, alreadyJoined: boolean}>}
 */
export function joinWaitlist(phone, source = 'web-landing') {
  return request('/waitlist', { method: 'POST', body: JSON.stringify({ phone, source }) });
}

/** Local YYYY-MM-DD so the deck rotates at the person's midnight. */
function localCalendarDay() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}
